import { useState, useRef, useEffect, useMemo } from 'react';
import { DateRange } from 'react-date-range';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// 'quarter' isn't a unit dayjs understands out of the box - without this,
// subtract(i, 'quarter')/startOf('quarter') silently no-op, so the "last 3
// quarters" presets below all resolve to the same (current) quarter.
dayjs.extend(quarterOfYear);

const DateRangePicker = ({
	value,
	onChange,
	className = '',
	placeholder = 'Select period',
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [dateRange, setDateRange] = useState([
		{
			startDate: new Date(),
			endDate: new Date(),
			key: 'selection',
		},
	]);
	const [selectionCount, setSelectionCount] = useState(0);
	const [isCustomMode, setIsCustomMode] = useState(false);
	const dropdownRef = useRef(null);

	// Generate preset options
	const getPresetOptions = () => {
		const now = dayjs();
		const yearStart = now.startOf('year');

		const presets = [
			{
				label: 'YTD (Year to Date)',
				value: 'YTD',
				startDate: yearStart.toDate(),
				endDate: now.toDate(),
			},
		];

		// Add last 3 quarters
		for (let i = 0; i < 3; i++) {
			const quarterStart = now.subtract(i, 'quarter').startOf('quarter');
			const quarterEnd = now.subtract(i, 'quarter').endOf('quarter');
			const year = quarterStart.year();
			const quarter = Math.floor(quarterStart.month() / 3) + 1;

			presets.push({
				label: `Q${quarter} ${year}`,
				value: `Q${quarter}_${year}`,
				startDate: quarterStart.toDate(),
				endDate: quarterEnd.toDate(),
			});
		}

		// Add last 3 months
		for (let i = 0; i < 3; i++) {
			const monthStart = now.subtract(i, 'month').startOf('month');
			const monthEnd = now.subtract(i, 'month').endOf('month');

			presets.push({
				label: monthStart.format('MMMM YYYY'),
				value: monthStart.format('YYYY-MM'),
				startDate: monthStart.toDate(),
				endDate: monthEnd.toDate(),
			});
		}

		// Add custom range option
		presets.push({
			label: 'Custom Range',
			value: 'custom',
			startDate: null,
			endDate: null,
		});

		return presets;
	};

	const presetOptions = useMemo(() => getPresetOptions(), []);
	const [selectedPreset, setSelectedPreset] = useState(value?.preset || 'YTD');

	// Update selectedPreset when value prop changes (but not when we're in custom mode)
	useEffect(() => {
		if (value?.preset && value.preset !== selectedPreset && !isCustomMode) {
			setSelectedPreset(value.preset);
			// Reset custom mode when preset changes from outside
			if (value.preset !== 'custom') {
				setIsCustomMode(false);
			}
		}
	}, [value?.preset, selectedPreset, isCustomMode]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
				setIsCustomMode(false); // Also reset custom mode when clicking outside
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Initialize date range based on current value
	useEffect(() => {
		const preset = presetOptions.find((p) => p.value === value?.preset);
		if (preset && preset.startDate && preset.endDate) {
			setDateRange([
				{
					startDate: new Date(preset.startDate),
					endDate: new Date(preset.endDate),
					key: 'selection',
				},
			]);
		} else if (value?.preset === 'custom' && value?.from && value?.to) {
			// For custom ranges, use the from/to dates from the value
			setDateRange([
				{
					startDate: new Date(value.from),
					endDate: new Date(value.to),
					key: 'selection',
				},
			]);
		}
	}, [value, presetOptions]);

	const handlePresetSelect = (preset, event) => {
		// Prevent event bubbling that might cause dropdown to close
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		if (preset.value === 'custom') {
			// For custom range, set the selected preset and custom mode immediately
			setSelectedPreset('custom');
			setIsCustomMode(true);
			setSelectionCount(0);

			// Initialize with today's date immediately (not in setTimeout)
			const today = new Date();
			setDateRange([
				{
					startDate: today,
					endDate: today,
					key: 'selection',
				},
			]);

			// Force dropdown to stay open
			setIsOpen(true);
			return;
		}

		// For other presets, proceed normally
		setSelectedPreset(preset.value);
		setIsCustomMode(false);

		if (preset.startDate && preset.endDate) {
			setDateRange([
				{
					startDate: preset.startDate,
					endDate: preset.endDate,
					key: 'selection',
				},
			]);

			// Call onChange with from/to dates
			onChange({
				preset: preset.value,
				from: dayjs(preset.startDate).format('YYYY-MM-DD'),
				to: dayjs(preset.endDate).format('YYYY-MM-DD'),
				label: preset.label,
			});

			setIsOpen(false);
		}
	};

	const handleDateRangeChange = (ranges) => {
		const { selection } = ranges;
		setDateRange([selection]);

		// For custom range, track the selection process
		if (selectedPreset === 'custom') {
			// Increment selection count on each interaction
			setSelectionCount((prev) => prev + 1);

			// On first click, we just have a start date (end date might be same as start)
			// On second click, we should have a proper range
			if (selection.startDate && selection.endDate) {
				const startTime = selection.startDate.getTime();
				const endTime = selection.endDate.getTime();

				// Check if this is a meaningful second selection
				// Either dates are different OR this is at least the second click
				if (startTime !== endTime || selectionCount >= 2) {
					const from = dayjs(selection.startDate).format('YYYY-MM-DD');
					const to = dayjs(selection.endDate).format('YYYY-MM-DD');
					const label =
						from === to
							? dayjs(selection.startDate).format('MMM D, YYYY')
							: `${dayjs(selection.startDate).format('MMM D')} - ${dayjs(
									selection.endDate
							  ).format('MMM D, YYYY')}`;

					onChange({
						preset: 'custom',
						from,
						to,
						label,
					});

					// Reset selection count and close dropdown
					setSelectionCount(0);
					setIsCustomMode(false);
					setIsOpen(false);
				}
			}
		}
	};

	const getDisplayLabel = () => {
		// First check if we have a label in the value object (for any preset including custom)
		if (value?.label) {
			return value.label;
		}

		// If we have custom preset and dates in value object, format them
		if (selectedPreset === 'custom' && value?.from && value?.to) {
			const fromDate = dayjs(value.from);
			const toDate = dayjs(value.to);

			if (fromDate.isSame(toDate, 'day')) {
				return fromDate.format('MMM D, YYYY');
			}

			if (fromDate.isSame(toDate, 'year')) {
				return `${fromDate.format('MMM D')} - ${toDate.format('MMM D, YYYY')}`;
			}

			return `${fromDate.format('MMM D, YYYY')} - ${toDate.format(
				'MMM D, YYYY'
			)}`;
		}

		// If we have a custom range in dateRange state (during selection)
		if (
			selectedPreset === 'custom' &&
			dateRange[0]?.startDate &&
			dateRange[0]?.endDate
		) {
			const fromDate = dayjs(dateRange[0].startDate);
			const toDate = dayjs(dateRange[0].endDate);

			if (fromDate.isSame(toDate, 'day')) {
				return fromDate.format('MMM D, YYYY');
			}

			if (fromDate.isSame(toDate, 'year')) {
				return `${fromDate.format('MMM D')} - ${toDate.format('MMM D, YYYY')}`;
			}

			return `${fromDate.format('MMM D, YYYY')} - ${toDate.format(
				'MMM D, YYYY'
			)}`;
		}

		// For preset options, try to find the matching preset label
		const preset = presetOptions.find((p) => p.value === selectedPreset);
		if (preset) {
			return preset.label;
		}

		// Fallback to formatting dates from value if available
		if (value?.from && value?.to) {
			const fromDate = dayjs(value.from);
			const toDate = dayjs(value.to);

			if (fromDate.isSame(toDate, 'day')) {
				return fromDate.format('MMM D, YYYY');
			}

			if (fromDate.isSame(toDate, 'year')) {
				return `${fromDate.format('MMM D')} - ${toDate.format('MMM D, YYYY')}`;
			}

			return `${fromDate.format('MMM D, YYYY')} - ${toDate.format(
				'MMM D, YYYY'
			)}`;
		}

		return placeholder;
	};

	return (
		<div className={`relative ${className}`} ref={dropdownRef}>
			<button
				type='button'
				onClick={() => setIsOpen(!isOpen)}
				className='form-select w-full flex items-center justify-between'
			>
				<span className='truncate'>{getDisplayLabel()}</span>
				<ChevronDownIcon className='h-4 w-4 text-gray-500 ml-2 flex-shrink-0' />
			</button>

			{isOpen && (
				<div className='absolute z-50 mt-1 bg-white border border-gray-300 rounded-lgx shadow-lg'>
					{/* Preset Options */}
					<div className='max-h-60 overflow-y-auto border-b border-gray-700'>
						{presetOptions.map((preset) => (
							<button
								key={preset.value}
								onClick={(event) => handlePresetSelect(preset, event)}
								className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
									selectedPreset === preset.value
										? 'bg-black text-white'
										: 'text-gray-900'
								}`}
							>
								{preset.label}
							</button>
						))}
					</div>

					{/* Date Range Picker (shown when custom is selected) */}
					{(() => {
						const shouldShow = selectedPreset === 'custom' || isCustomMode;

						return shouldShow;
					})() && (
						<div className='p-2'>
							<DateRange
								ranges={dateRange}
								onChange={handleDateRangeChange}
								months={1}
								direction='horizontal'
								showSelectionPreview={true}
								moveRangeOnFirstSelection={false}
								editableDateInputs={true}
								maxDate={new Date()}
								rangeColors={['#007bff']}
								showDateDisplay={false}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default DateRangePicker;
